/**
 * Shared discovery + read-menu phase builders — extracted from the
 * original monolithic `Simulation.tsx::buildPhases` per ADR-0159 §3
 * ("Journey builders share the LIVE discovery + LIVE read-menu phase
 * constructors — the diverging content is what comes AFTER identity").
 *
 * Every per-protocol journey (except `unverified`, which routes through
 * its own blocked-path shape) begins with `buildDiscoveryPhase` +
 * `buildReadMenuPhase`. The transactional phases that follow diverge by
 * protocol and live in the per-protocol modules.
 *
 * Also exports the cart / product helpers used by the transactional
 * journeys (mcp / acp / a2a-ap2) that render a full checkout illustration.
 * Discovery-only and unidentifiable journeys don't call them.
 */

import type { CatalogProduct } from '@sill/types';

import type { LiveResult } from '../live/fetch-surface.js';
import type { AgentDef } from '../roster.js';
import type { CartProduct, EndpointInfo, LiveProducts, Phase } from './types.js';

const CURRENCY_SYMBOLS: Readonly<Record<string, string>> = {
  USD: '$',
  CAD: '$',
  AUD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
};

export function currencySymbol(code?: string): string {
  if (code === undefined || code.length === 0) return '$';
  const up = code.toUpperCase();
  return CURRENCY_SYMBOLS[up] ?? `${up} `;
}

/**
 * Format a minor-unit amount for display. USD (and most currencies) use
 * two decimals; JPY has no minor unit, so the stored value IS the major
 * value. Never used for math beyond × quantity + a flat tax/shipping
 * illustration.
 */
export function formatMinor(minor: number, code: string | undefined): string {
  const sym = currencySymbol(code);
  const up = (code ?? 'USD').toUpperCase();
  if (up === 'JPY') return `${sym}${Math.round(minor)}`;
  const major = minor / 100;
  return `${sym}${major.toFixed(2)}`;
}

/**
 * Pick the most realistic representative product for the cart illustration:
 *   1. first `is_eligible_checkout` (what an agent could actually buy)
 *   2. else first `is_eligible_search` (what an agent would see)
 *   3. else first product
 * Returns null when the page is empty.
 */
export function pickCartProduct(
  products: readonly CatalogProduct[],
): CartProduct | null {
  if (products.length === 0) return null;
  const chosen =
    products.find((p) => p.is_eligible_checkout)
    ?? products.find((p) => p.is_eligible_search)
    ?? products[0]!;
  const symbol = currencySymbol(chosen.currency);
  if (chosen.price_minor === undefined) {
    return {
      title: chosen.title,
      unit_minor: null,
      symbol,
      is_sample: false,
      unit_price_display: '',
      line_total_display: '',
      total_display: '',
    };
  }
  const unit = chosen.price_minor;
  const line = unit * 2;
  // Flat 15% tax+shipping illustration (matches the original "$48 → $55.20"
  // ratio; wired through the narration only, no math beyond this).
  const total = Math.round(line * 1.15);
  return {
    title: chosen.title,
    unit_minor: unit,
    symbol,
    is_sample: false,
    unit_price_display: formatMinor(unit, chosen.currency),
    line_total_display: formatMinor(line, chosen.currency),
    total_display: formatMinor(total, chosen.currency),
  };
}

export const SAMPLE_FALLBACK: CartProduct = {
  title: 'Sample Product',
  unit_minor: 2400,
  symbol: '$',
  is_sample: true,
  unit_price_display: '$24.00',
  line_total_display: '$48.00',
  total_display: '$55.20',
};

/** Representative product samples for the Read-menu phase (count + up to
 *  three formatted lines). */
export interface ProductSamples {
  readonly count: number;
  readonly lines: readonly string[];
  readonly first_title: string | null;
  readonly first_price: string | null;
}

export function summarizeProducts(
  products: readonly CatalogProduct[],
): ProductSamples {
  const lines: string[] = [];
  for (const p of products.slice(0, 3)) {
    const price =
      p.price_minor !== undefined ? formatMinor(p.price_minor, p.currency) : '';
    lines.push(price.length > 0 ? `${p.title} · ${price}` : p.title);
  }
  const first = products[0];
  const first_title = first?.title ?? null;
  const first_price =
    first?.price_minor !== undefined
      ? formatMinor(first.price_minor, first.currency)
      : null;
  return { count: products.length, lines, first_title, first_price };
}

/**
 * Small live-endpoint builder — turns an optional {@link LiveResult} into
 * an {@link EndpointInfo} row. Kept here (shared across builders) rather
 * than the types module since it's a runtime helper.
 */
export function liveEndpoint(
  method: 'GET' | 'POST',
  url: string,
  result: LiveResult | undefined,
): EndpointInfo {
  if (!result) {
    return { method, path: url, status: 0, ms: 0, body: 'fetching live…', live: true };
  }
  return { method, path: url, status: result.status, ms: result.ms, body: result.body, live: true };
}

/**
 * Documented-UA caption + honest masquerade / robots notes — the
 * teaching-layer captions attached to every LIVE GET row for the picked
 * agent.
 */
export function uaCaption(agent: AgentDef): string {
  return `User-Agent ${agent.name} sends in the real world — documented signature`;
}

export function masqueradeNoteFor(agent: AgentDef): string | null {
  if (agent.declared) return null;
  if (agent.id === 'unverified') {
    return 'No bot identity in the User-Agent — no signature either.';
  }
  return 'Masquerades as a browser — no bot identity in the User-Agent.';
}

export function robotsNoteFor(agent: AgentDef): string {
  return agent.honorsRobots ? 'honored' : 'ignored — fetches regardless';
}

// ─── Phase builders — shared across every non-blocked journey ────────

export function buildDiscoveryPhase(
  agent: AgentDef,
  installed: boolean,
  cardUrl: string,
  cardResult: LiveResult | undefined,
): Phase {
  // In sample mode `cardUrl` is the malformed-key SENTINEL
  // (`…/agent-card/invalid.json`) — show the placeholder path instead.
  const cardShort = installed
    ? cardUrl.replace('https://', '')
    : '/v1/agent-card/{your_site_key}.json';
  const uaCap = uaCaption(agent);
  const masq = masqueradeNoteFor(agent);
  return {
    id: 'discover',
    name: 'Discovery',
    tier: 'live',
    narration: installed
      ? cardResult
        ? `${agent.name} found your store and read your signed agent card — live, in ${cardResult.ms} ms. No human, no scraping.`
        : `${agent.name} is reading your signed agent card…`
      : `${agent.name} would read your signed agent card here.`,
    endpoint: installed
      ? {
          ...liveEndpoint('GET', cardUrl, cardResult),
          userAgent: agent.userAgent,
          userAgentCaption: uaCap,
          masqueradeNote: masq,
        }
      : {
          method: 'GET',
          path: '/v1/agent-card/{your_site_key}.json',
          status: 200,
          ms: 47,
          body: '{ "name": "Your store", "skills": [ … ] }',
          live: false,
          userAgent: agent.userAgent,
          userAgentCaption: uaCap,
          masqueradeNote: masq,
        },
    ok: true,
  };
}

export function buildReadMenuPhase(
  agent: AgentDef,
  installed: boolean,
  catalogUrl: string,
  catalogResult: LiveResult | undefined,
  liveProducts: LiveProducts,
): Phase {
  // Same sentinel guard as `buildDiscoveryPhase` — never render the
  // `…/catalog/invalid.json` fallback URL.
  const catShort = installed
    ? catalogUrl.replace('https://', '')
    : '/v1/catalog/{your_site_key}.json';
  const uaCap = uaCaption(agent);
  const masq = masqueradeNoteFor(agent);
  const samples =
    liveProducts.kind === 'resolved' ? summarizeProducts(liveProducts.products) : null;
  const hasProducts = samples !== null && samples.count > 0;
  return {
    id: 'browse',
    name: 'Read menu',
    tier: 'live',
    narration: installed
      ? hasProducts
        ? `${agent.name} browsed your catalog — live. The response is your real synced menu.`
        : liveProducts.kind === 'resolved'
          ? `Your store has 0 products synced — an agent browsing finds nothing to buy.`
          : catalogResult
            ? `It read your published catalog — live, in ${catalogResult.ms} ms. Discovery is open to every agent; your menu is public by design.`
            : `${agent.name} is reading your published catalog…`
      : `${agent.name} would read your published catalog (ARD) here.`,
    endpoint: installed
      ? hasProducts
        ? {
            method: 'GET',
            path: catalogUrl,
            status: 200,
            ms: catalogResult?.ms ?? 0,
            body: `${samples!.count} product${samples!.count === 1 ? '' : 's'}:\n  ${samples!.lines.join('\n  ')}${samples!.count > samples!.lines.length ? `\n  … (+${samples!.count - samples!.lines.length} more)` : ''}`,
            live: true,
            userAgent: agent.userAgent,
            userAgentCaption: uaCap,
            masqueradeNote: masq,
          }
        : liveProducts.kind === 'resolved'
          ? {
              method: 'GET',
              path: catalogUrl,
              status: 200,
              ms: catalogResult?.ms ?? 0,
              body: '0 products synced.',
              live: true,
              userAgent: agent.userAgent,
              userAgentCaption: uaCap,
              masqueradeNote: masq,
            }
          : {
              ...liveEndpoint('GET', catalogUrl, catalogResult),
              userAgent: agent.userAgent,
              userAgentCaption: uaCap,
              masqueradeNote: masq,
            }
      : {
          method: 'GET',
          path: '/v1/catalog/{your_site_key}.json',
          status: 200,
          ms: 60,
          body: '{ "skills": [ … ] }',
          live: false,
          userAgent: agent.userAgent,
          userAgentCaption: uaCap,
          masqueradeNote: masq,
        },
    ok: true,
  };
}
